import { authenticatedFetch } from './ApiInterceptor';
import { API_BASE_URL } from './Constants';

/**
 * Registers a device token with the backend
 * 
 * @param {string} deviceToken - The Firebase FCM token
 * @param {boolean} enabled - Whether notifications are enabled
 * @returns {Promise<Object>} - Response from the server
 */
export const registerDeviceToken = async (deviceToken, enabled = true) => {
  console.log("=== START: registerDeviceToken API Call ===");
  console.log("Device token:", deviceToken);
  console.log("Enabled:", enabled);
  
  try {
    // Check authentication
    const isAuthenticated = !!localStorage.getItem("access_token");
    console.log("Is user authenticated:", isAuthenticated);
    
    if (!isAuthenticated) {
      console.error("User not authenticated, cannot register device token");
      return { success: false, error: "User not authenticated" };
    }
    
    const deviceType = detectDeviceType();
    const payload = {
      device_id: deviceToken,  // Changed from device_token to device_id
      notifications_enabled: enabled,
      device_type: deviceType
    };
    
    console.log("Request payload:", payload);
    console.log("Request URL:", `${API_BASE_URL}/accounts/device_id/`);
    
    // Use authenticatedFetch to ensure auth headers are included
    const response = await authenticatedFetch('/accounts/device_id/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    
    console.log("Response status:", response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to register device: ${response.status} ${response.statusText}`, errorText);
      throw new Error(`Failed to register device: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log("Registration successful, response data:", data);
    
    // Store success in localStorage
    localStorage.setItem('fcm_token_sent', 'true');
    
    return { success: true, data };
  } catch (error) {
    console.error('Error registering device token:', error);
    return { success: false, error: error.message };
  } finally {
    console.log("=== END: registerDeviceToken API Call ===");
  }
};

/**
 * Updates notification status when user toggles browser permissions
 * 
 * @param {boolean} enabled - Whether notifications are enabled
 * @returns {Promise<Object>} - Response from the server
 */
export const updateNotificationStatus = async (enabled) => {
  console.log(`=== START: updateNotificationStatus(${enabled}) ===`);
  
  try {
    const isAuthenticated = !!localStorage.getItem("access_token");
    if (!isAuthenticated) {
      console.error("User not authenticated, cannot update notification status");
      return { success: false, error: "User not authenticated" };
    }
    
    // Get the stored token
    const deviceToken = localStorage.getItem('fcm_token');
    if (!deviceToken) {
      console.error("No device token found in localStorage");
      
      // If notifications are being disabled and we don't have a token,
      // we can consider this a "success" since we're in the desired state
      if (!enabled) {
        console.log("Notification status already effectively disabled (no token)");
        return { success: true, message: "Notifications already disabled" };
      }
      
      return { success: false, error: "No device token available" };
    }
    
    const deviceType = detectDeviceType();
    const payload = {
      device_id: deviceToken,
      notifications_enabled: enabled,
      device_type: deviceType
    };
    
    console.log("PATCH payload:", payload);
    
    // Use PATCH to update only the notification status
    const response = await authenticatedFetch('/accounts/device_id/', {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Failed to update notification status: ${response.status}`, errorText);
      throw new Error(`Failed to update notification status: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log("Update successful, response:", data);
    
    return { success: true, data };
  } catch (error) {
    console.error("Error updating notification status:", error);
    return { success: false, error: error.message };
  } finally {
    console.log(`=== END: updateNotificationStatus ===`);
  }
};

/**
 * Checks the notification status from the backend
 * 
 * @returns {Promise<Object>} - Current notification status
 */
export const checkNotificationStatus = async () => {
  try {
    const response = await authenticatedFetch('/accounts/device_id/', {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error(`Failed to check notification status: ${response.status}`);
    }

    const data = await response.json();
    return { success: true, enabled: !!data.notifications_enabled, data };
  } catch (error) {
    console.error('Error checking notification status:', error);
    return { success: false, enabled: false, error: error.message };
  }
};

/**
 * Detects the device type for registration
 * 
 * @returns {string} - 'web', 'android', or 'ios'
 */
const detectDeviceType = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  console.log("User agent:", userAgent);
  
  // Check if mobile
  if (/android/i.test(userAgent)) {
    console.log("Detected device: Android");
    return 'android';
  }
  
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    console.log("Detected device: iOS");
    return 'ios';
  }
  
  // Default to web for desktop browsers
  console.log("Detected device: Web (desktop)");
  return 'web';
};
